import { useEffect, useState } from 'react';
import { getWxLoginStyleHref } from './wx-login-style';

type BootstrapDisabled = {
  configured: false;
  message: string;
};

type BootstrapOk = {
  configured: true;
  appId: string;
  redirectUri: string;
  state: string;
  qrconnectUrl: string;
};

type ApiEnvelope<T> = {
  success?: boolean;
  data: T;
  message?: string;
};

declare global {
  interface Window {
    WxLogin?: new (opts: Record<string, unknown>) => void;
  }
}

/**
 * 加载开放平台 WxLogin 二维码（与 wechat-open-test 一致）
 */
export function useWeChatQrLogin(enabled: boolean, returnUrl?: string | null) {
  const [bootstrap, setBootstrap] = useState<BootstrapDisabled | BootstrapOk | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ mode: 'login' });
        if (returnUrl?.trim()) {
          params.set('returnUrl', returnUrl.trim());
        }
        const res = await fetch(`/api/wechat/open/bootstrap?${params.toString()}`);
        const json = (await res.json()) as ApiEnvelope<BootstrapDisabled | BootstrapOk>;
        if (!cancelled) {
          setBootstrap(json.data);
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, returnUrl]);

  useEffect(() => {
    if (!enabled || !bootstrap || !bootstrap.configured) return;

    const el = document.getElementById('wx_login_container');
    if (!el) return;

    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
    script.async = true;
    script.onload = () => {
      if (!window.WxLogin) return;
      el.innerHTML = '';
      new window.WxLogin({
        self_redirect: false,
        id: 'wx_login_container',
        appid: bootstrap.appId,
        scope: 'snsapi_login',
        redirect_uri: encodeURIComponent(bootstrap.redirectUri),
        state: bootstrap.state,
        style: 'black',
        href: getWxLoginStyleHref(),
        fast_login: true,
      });
    };
    script.onerror = () => setLoadError('无法加载微信登录组件');
    document.body.appendChild(script);

    return () => {
      script.remove();
      el.innerHTML = '';
    };
  }, [enabled, bootstrap]);

  return { bootstrap, loadError };
}
